import { jsxs, jsx } from "react/jsx-runtime";
import { forwardRef, useMemo, useCallback } from "react";
import PropTypes from "prop-types";
import { range } from "lodash/fp";
import { startOfMonth, getYear, format, isBefore, isAfter, isSameMonth } from "date-fns/fp";
import { Calendar } from "../calendar/calendar.es.js";
import { CalendarBody } from "../calendar/calendar-body.es.js";
import "../calendar/calendar-footer.es.js";
import { CalendarGrid } from "../calendar/calendar-grid.es.js";
import { CalendarHeader } from "../calendar/calendar-header.es.js";
import { CalendarOption } from "../calendar/calendar-option.es.js";
import "../calendar/calendar-weekdays.es.js";
import { useMonthPicker } from "./month-picker.hooks.es.js";
const propTypes = {
  inputRef: PropTypes.shape({
    current: PropTypes.instanceOf(Element)
  }),
  maxDate: PropTypes.instanceOf(Date),
  minDate: PropTypes.instanceOf(Date),
  onCancel: PropTypes.func.isRequired,
  onSelectAndClose: PropTypes.func.isRequired,
  value: PropTypes.instanceOf(Date)
};
const defaultProps = {
  inputRef: void 0,
  maxDate: void 0,
  minDate: void 0,
  value: void 0
};
const MonthPicker = forwardRef(
  ({ inputRef, maxDate, minDate, onCancel, onSelectAndClose, value }, ref) => {
    const minMonth = useMemo(
      () => minDate ? startOfMonth(minDate) : void 0,
      [minDate]
    );
    const maxMonth = useMemo(
      () => maxDate ? startOfMonth(maxDate) : void 0,
      [maxDate]
    );
    const {
      highlightEndOfYear,
      highlightNextDecade,
      highlightNextMonth,
      highlightNextQuarter,
      highlightNextYear,
      highlightPreviousDecade,
      highlightPreviousMonth,
      highlightPreviousQuarter,
      highlightPreviousYear,
      highlightStartOfYear,
      highlightedDate,
      setHighlightedDate,
      viewNextDecade,
      viewNextYear,
      viewPreviousDecade,
      viewPreviousYear,
      yearInView
    } = useMonthPicker({
      initialValue: value,
      maxMonth,
      minMonth
    });
    const monthsInView = useMemo(
      () => range(0, 12).map((m) => new Date(yearInView, m, 1)),
      [yearInView]
    );
    const handleKeyDown = useCallback(
      (event) => {
        event.stopPropagation();
        switch (event.key) {
          case "ArrowDown":
            highlightNextQuarter();
            break;
          case "ArrowLeft":
            highlightPreviousMonth();
            break;
          case "ArrowRight":
            highlightNextMonth();
            break;
          case "ArrowUp":
            highlightPreviousQuarter();
            break;
          case "End":
            highlightEndOfYear();
            break;
          case "Enter":
            if (highlightedDate) {
              onSelectAndClose(highlightedDate);
            }
            break;
          case "Escape":
            onCancel();
            break;
          case "Home":
            highlightStartOfYear();
            break;
          case "PageDown":
            if (event.shiftKey) {
              highlightNextDecade();
            } else {
              highlightNextYear();
            }
            break;
          case "PageUp":
            if (event.shiftKey) {
              highlightPreviousDecade();
            } else {
              highlightPreviousYear();
            }
            break;
        }
      },
      [
        highlightEndOfYear,
        highlightNextDecade,
        highlightNextMonth,
        highlightNextQuarter,
        highlightNextYear,
        highlightPreviousDecade,
        highlightPreviousMonth,
        highlightPreviousQuarter,
        highlightPreviousYear,
        highlightStartOfYear,
        highlightedDate,
        onCancel,
        onSelectAndClose
      ]
    );
    return /* @__PURE__ */ jsxs(
      Calendar,
      {
        ref,
        inputRef,
        onCancel,
        onKeyDown: handleKeyDown,
        children: [
          /* @__PURE__ */ jsx(
            CalendarHeader,
            {
              first: minDate && getYear(minDate) === yearInView,
              last: maxDate && getYear(maxDate) === yearInView,
              onNext: viewNextYear,
              onPrevious: viewPreviousYear,
              onSkipBack: viewPreviousDecade,
              onSkipForward: viewNextDecade,
              children: yearInView
            }
          ),
          /* @__PURE__ */ jsx(CalendarBody, { children: /* @__PURE__ */ jsx(CalendarGrid, { role: "listbox", columns: 3, magnitude: "large", children: monthsInView.map((month) => /* @__PURE__ */ jsx(
            CalendarOption,
            {
              "aria-label": format("MMMM yyyy", month),
              external: isBefore(minDate, month) || isAfter(maxDate, month),
              highlighted: isSameMonth(month, highlightedDate),
              onHighlight: setHighlightedDate,
              onSelect: onSelectAndClose,
              selected: isSameMonth(month, value),
              value: month,
              variant: "month",
              children: format("MMM", month)
            },
            month.getTime()
          )) }) })
        ]
      }
    );
  }
);
MonthPicker.propTypes = propTypes;
MonthPicker.defaultProps = defaultProps;
export {
  MonthPicker
};
